import { Component } from "@theme/component";

/** Keep native signup tests on HTTPS without changing the active theme. */
export function secureSignupPreviewUrl(shopUrl, currentLocation, theme) {
  if (
    currentLocation.protocol !== "http:" ||
    !["127.0.0.1", "localhost"].includes(currentLocation.hostname) ||
    !["development", "unpublished"].includes(theme?.role) ||
    !Number.isSafeInteger(theme?.id) ||
    theme.id <= 0
  )
    return null;
  try {
    const shop = new URL(shopUrl);
    if (shop.protocol !== "https:" || shop.username || shop.password)
      return null;
    const preview = new URL(
      currentLocation.pathname + currentLocation.search,
      shop.origin,
    );
    preview.searchParams.set("preview_theme_id", String(theme.id));
    preview.searchParams.set("raptor_dev_qa", "1");
    preview.searchParams.set("raptor_demo", "off");
    return preview;
  } catch {
    return null;
  }
}

// These review controls never submit contacts or confirm real consent.
function signupDemoAllowed() {
  const local = ["127.0.0.1", "localhost"].includes(location.hostname);
  const draft =
    ["development", "unpublished"].includes(window.Shopify?.theme?.role) &&
    Number.isSafeInteger(window.Shopify?.theme?.id) &&
    window.Shopify.theme.id > 0;
  const scenario = new URL(location.href).searchParams.get("raptor_demo");
  return (
    (local || draft) &&
    ["base", "promo", "scheduled", "inactive", "error", "keep"].includes(
      scenario,
    )
  );
}
const phoneRules = {
  US: { prefix: "1", min: 10, max: 10 },
  CA: { prefix: "1", min: 10, max: 10 },
  AU: { prefix: "61", min: 9, max: 9 },
  NZ: { prefix: "64", min: 8, max: 10 },
  GB: { prefix: "44", min: 10, max: 10 },
};

export function normalizePreviewPhone(value, country) {
  const rule = phoneRules[country];
  if (!rule || !/^\+?[\d\s().-]+$/.test(value.trim())) return null;
  let digits = value.replace(/\D/g, "");
  if (value.trim().startsWith("+")) {
    if (!digits.startsWith(rule.prefix)) return null;
    digits = digits.slice(rule.prefix.length);
  } else if (country === "US" || country === "CA") {
    if (digits.length === 11 && digits.startsWith("1"))
      digits = digits.slice(1);
  } else {
    digits = digits.replace(/^0/, "");
  }
  if (digits.length < rule.min || digits.length > rule.max) return null;
  return `+${rule.prefix}${digits}`;
}

class RaptorSignupPreview extends Component {
  #controller;
  #timer;
  #emailGranted = false;
  #smsGranted = false;
  #refreshQueued = false;
  #observer = new MutationObserver(() => {
    if (!this.hasAttribute("data-preview-ready") && !this.#refreshQueued) {
      this.#refreshQueued = true;
      queueMicrotask(() => {
        this.#refreshQueued = false;
        if (this.isConnected) this.#initialize();
      });
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ["data-preview-ready"],
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearTimeout(this.#timer);
    this.#observer.disconnect();
  }

  #initialize() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.smsEnabled = this.dataset.smsEnabled === "true";
    this.addEventListener("submit", this.#submit, { signal });
    this.addEventListener("click", this.#click, { signal });
    this.addEventListener("change", this.#change, { signal });
    this.addEventListener("input", this.#clearValidity, { signal });
    this.previewAllowed =
      signupDemoAllowed() && this.dataset.nativeCapture !== "true";
    for (const fieldset of this.querySelectorAll("fieldset"))
      fieldset.disabled = !this.previewAllowed;
    const stateSelect = this.querySelector("[data-preview-state]");
    if (stateSelect instanceof HTMLSelectElement)
      stateSelect.disabled = !this.previewAllowed;
    for (const control of this.querySelectorAll(
      "[data-preview-action], .raptor-signup__review",
    ))
      control.hidden = !this.previewAllowed;
    this.setAttribute("data-preview-ready", "true");
    this.#show(
      "email",
      this.previewAllowed ? "" : this.dataset.unconfiguredStatus,
      false,
    );
  }

  #show(state, message = "", focus = true) {
    if (!this.smsEnabled && ["sms", "pending", "complete"].includes(state)) {
      state = "email-done";
      message = this.#emailGranted ? this.dataset.emailStatus : "";
    }
    clearTimeout(this.#timer);
    this.removeAttribute("aria-busy");
    for (const panel of this.querySelectorAll("[data-signup-state]")) {
      const captureSms =
        this.smsEnabled &&
        this.dataset.nativeCapture === "true" &&
        ["email", "email-done"].includes(state) &&
        panel.dataset.signupState === "sms";
      panel.hidden =
        (!this.smsEnabled && panel.dataset.signupState === "sms") ||
        (panel.dataset.signupState !== state && !captureSms);
      for (const button of panel.querySelectorAll("button"))
        button.disabled = false;
    }
    const status = this.querySelector('[role="status"]');
    if (status) status.textContent = message;
    const select = this.querySelector("[data-preview-state]");
    if (select instanceof HTMLSelectElement) select.value = state;
    if (focus)
      this.querySelector(`[data-signup-state="${state}"] h2`)?.focus({
        preventScroll: true,
      });
  }

  #submit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    if (!form.matches("[data-preview-form]")) return;
    event.preventDefault();
    if (!this.previewAllowed) return;
    if (this.hasAttribute("aria-busy") || !form.reportValidity()) return;
    if (form.dataset.previewForm === "email" && this.#emailGranted) {
      this.#show("already", this.dataset.alreadyStatus);
      return;
    }
    if (form.dataset.previewForm === "sms") {
      if (!this.smsEnabled) return;
      const phone = form.querySelector('input[type="tel"]');
      const country = form.querySelector("[data-phone-country]");
      const consent = form.querySelector("[data-sms-consent]");
      if (
        !(phone instanceof HTMLInputElement) ||
        !(country instanceof HTMLSelectElement)
      )
        return;
      if (!normalizePreviewPhone(phone.value, country.value)) {
        phone.setCustomValidity(this.dataset.phoneError || "");
        phone.reportValidity();
        return;
      }
      if (!(consent instanceof HTMLInputElement) || !consent.checked) return;
    }
    this.setAttribute("aria-busy", "true");
    const status = this.querySelector('[role="status"]');
    if (status) status.textContent = this.dataset.loadingStatus || "";
    for (const button of form.querySelectorAll("button"))
      button.disabled = true;
    this.#timer = setTimeout(() => {
      if (form.dataset.previewForm === "email") {
        this.#emailGranted = true;
        this.#show("email-done", this.dataset.emailStatus);
      } else {
        this.#show("pending", this.dataset.pendingStatus);
      }
    }, 350);
  };

  #click = (event) => {
    if (!this.previewAllowed || !(event.target instanceof Element)) return;
    const button = event.target.closest("[data-preview-action]");
    if (!(button instanceof HTMLButtonElement) || !this.contains(button))
      return;
    const action = button.dataset.previewAction;
    if (!this.smsEnabled && ["confirm", "sms"].includes(action)) return;
    if (action === "confirm") {
      const wasGranted = this.#smsGranted;
      this.#smsGranted = true;
      this.#show(
        "complete",
        wasGranted ? this.dataset.alreadyStatus : this.dataset.confirmedStatus,
      );
    } else if (action === "skip") {
      this.#show(
        "complete",
        this.#emailGranted ? this.dataset.emailStatus : "",
      );
    } else if (action === "email" || action === "sms") {
      this.#show(action);
    }
  };

  #change = (event) => {
    if (!this.previewAllowed || !(event.target instanceof HTMLSelectElement))
      return;
    if (event.target.matches("[data-preview-state]")) {
      const state = event.target.value;
      const messages = {
        pending: this.dataset.pendingStatus,
        already: this.dataset.alreadyStatus,
        error: this.dataset.errorStatus,
      };
      if (
        [
          "email",
          "email-done",
          "sms",
          "pending",
          "complete",
          "already",
          "error",
        ].includes(state)
      )
        this.#show(state, messages[state] || "", false);
    } else if (event.target.matches("[data-phone-country]")) {
      const phone = this.querySelector('input[type="tel"]');
      if (phone instanceof HTMLInputElement) phone.setCustomValidity("");
    }
  };

  #clearValidity = (event) => {
    if (event.target instanceof HTMLInputElement)
      event.target.setCustomValidity("");
  };
}

class RaptorPrizeGallery extends Component {
  #controller;
  #refreshQueued = false;
  #observer = new MutationObserver(() => {
    if (!this.hasAttribute("data-gallery-ready") && !this.#refreshQueued) {
      this.#refreshQueued = true;
      queueMicrotask(() => {
        this.#refreshQueued = false;
        if (this.isConnected) this.#initialize();
      });
    }
  });
  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ["data-gallery-ready"],
    });
  }
  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer.disconnect();
  }
  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener("click", this.#click, { signal });
    for (const button of this.querySelectorAll("[data-gallery-button]"))
      button.disabled = false;
    this.setAttribute("data-gallery-ready", "true");
    this.#select(
      this.querySelector("[data-gallery-button]")?.dataset.galleryButton,
    );
    if (window.Shopify?.designMode)
      document.addEventListener("shopify:block:select", this.#blockSelect, {
        signal,
      });
  }
  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest("[data-gallery-button]");
    if (button instanceof HTMLButtonElement && this.contains(button))
      this.#select(button.dataset.galleryButton);
  };
  #blockSelect = (event) => {
    const button = this.querySelector(
      `[data-gallery-button="${event.detail.blockId}"]`,
    );
    if (button) this.#select(button.dataset.galleryButton);
  };
  #select(id) {
    if (!id) return;
    let label = "";
    for (const button of this.querySelectorAll("[data-gallery-button]")) {
      const selected = button.dataset.galleryButton === id;
      button.setAttribute("aria-pressed", String(selected));
      if (selected)
        label = button.getAttribute("aria-label") || button.textContent.trim();
    }
    for (const panel of this.querySelectorAll("[data-gallery-panel]"))
      panel.hidden = panel.dataset.galleryPanel !== id;
    const status = this.querySelector('[role="status"]');
    if (status) status.textContent = label;
  }
}

class RaptorGiveawayFaq extends Component {
  #controller;
  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener("shopify:block:select", this.#select, {
      signal: this.#controller.signal,
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }
  #select = (event) => {
    if (!(event.target instanceof Element)) return;
    const details = event.target.closest("details");
    if (details && this.contains(details)) details.open = true;
  };
}

if (!customElements.get("raptor-signup-preview"))
  customElements.define("raptor-signup-preview", RaptorSignupPreview);
if (!customElements.get("raptor-prize-gallery"))
  customElements.define("raptor-prize-gallery", RaptorPrizeGallery);
if (!customElements.get("raptor-giveaway-faq"))
  customElements.define("raptor-giveaway-faq", RaptorGiveawayFaq);

/** Read the accepted native form identity only after its logged-in result is visible.
 * Contact values are passed in memory to the optional SMS consent step, never storage.
 */
export function nativeNinjaRegistration(doc) {
  const panel = doc?.querySelector("#content-logged-in");
  const style = panel && doc.defaultView?.getComputedStyle(panel);
  if (
    !panel ||
    !style ||
    style.display === "none" ||
    ["hidden", "collapse"].includes(style.visibility) ||
    !panel.getClientRects().length
  )
    return null;
  const input = doc.querySelector("#email-input");
  const email =
    input?.type === "email" && input.validity?.valid ? input.value.trim() : "";
  return { email: email || null };
}

/**
 * Whether the native form shows its signed-in panel, which Ninja renders only
 * for a registered entrant. Reads computed style, so it also works while the
 * popup is closed and the frame has no layout boxes.
 */
export function nativeNinjaEntered(doc) {
  const panel = doc?.querySelector("#content-logged-in");
  if (!panel) return false;
  const style = doc.defaultView?.getComputedStyle(panel);
  return Boolean(
    style &&
    style.display !== "none" &&
    !["hidden", "collapse"].includes(style.visibility),
  );
}

const RAPTOR_ENTRY_KEY = "tn:raptor-entered";

/** Remembers in this browser that the visitor entered this campaign. */
export function rememberRaptorEntry(campaignId, storage) {
  if (!campaignId) return;
  try {
    (storage ?? globalThis.localStorage)?.setItem(RAPTOR_ENTRY_KEY, campaignId);
  } catch {
    /* Without storage the live checks still run before each automatic open. */
  }
}

/** Whether this browser already recorded an entry for this campaign. */
export function rememberedRaptorEntry(campaignId, storage) {
  if (!campaignId) return false;
  try {
    return (
      (storage ?? globalThis.localStorage)?.getItem(RAPTOR_ENTRY_KEY) ===
      campaignId
    );
  } catch {
    return false;
  }
}

/** Only a Shopify-signed response for this exact customer may supply a balance. */
export function ninjaAccountOutcome(payload, campaignId, customerId) {
  if (
    !campaignId ||
    !customerId ||
    payload?.source !== "ninja" ||
    payload.campaignId !== campaignId ||
    payload.customerId !== `gid://shopify/Customer/${customerId}`
  )
    return null;
  if (
    payload.status === "GiveawayOrUserNotFound" &&
    !Object.hasOwn(payload, "totalEntries")
  )
    return "missing";
  if (
    (!payload.status || payload.status === "Success") &&
    Number.isSafeInteger(payload.totalEntries) &&
    payload.totalEntries >= 0
  )
    return "confirmed";
  return null;
}

/** Sales funnel signup. Session storage holds only a shown flag, never contacts.
 * @extends {Component<{dialog: HTMLDialogElement}>}
 */
class RaptorSignupPopup extends Component {
  static shown = false;
  requiredRefs = ["dialog"];
  connectedCallback() {
    super.connectedCallback();
    this.bind();
  }
  updatedCallback() {
    super.updatedCallback();
    this.bind();
  }
  bind() {
    this.abort?.abort();
    this.accountRequest?.abort();
    this.accountRequest = null;
    clearTimeout(this.accountTimer);
    clearTimeout(this.timer);
    this.abort = new AbortController();
    const { signal } = this.abort;
    const dialog = this.refs.dialog;
    this.klaviyoObserver?.disconnect();
    clearTimeout(this.klaviyoTimer);
    this.ninjaObserver?.disconnect();
    clearTimeout(this.ninjaTimer);
    this.ninjaResizeObserver?.disconnect();
    this.ninjaContentObserver?.disconnect();
    this.ninjaFrameAbort?.abort();
    this.ninjaDocumentAbort?.abort();
    cancelAnimationFrame(this.ninjaResizeFrame);
    this.ninjaFrame = null;
    this.ninjaResize = null;
    this.ninjaReady = false;
    this.ninjaStartTimeout = null;
    this.smsDefinitionPending = false;
    this.smsEnabled = this.dataset.smsEnabled === "true";
    this.querySelector("raptor-sms-signup")?.clearParticipant?.();
    const local = ["127.0.0.1", "localhost"].includes(location.hostname);
    const draft =
      ["development", "unpublished"].includes(window.Shopify?.theme?.role) &&
      Number.isSafeInteger(window.Shopify?.theme?.id) &&
      window.Shopify.theme.id > 0;
    // The merchant's "preview only" setting closes this on the published
    // theme; preview themes always open it for review.
    this.hidden =
      (!local && !draft && this.dataset.previewOnly === "true") ||
      this.dataset.marketEligible === "false" ||
      this.dataset.country === "NZ";
    if (this.hidden) {
      if (dialog.open) dialog.close();
      return;
    }
    this.accountEnabled = Boolean(
      this.dataset.customerId &&
      location.protocol === "https:" &&
      this.dataset.captureMode === "ninja" &&
      !signupDemoAllowed(),
    );
    this.accountState = "loading";
    this.nativeAccountRegistrationSeen = false;
    this.syncAccountPanel();
    if (this.dataset.captureMode === "ninja") {
      const host = this.querySelector("[data-ninja-host]");
      const status = this.querySelector("[data-ninja-status]");
      const review = this.querySelector("[data-ninja-review]");
      const preview = signupDemoAllowed();
      const localHttp = local && location.protocol === "http:";
      const securePreview = secureSignupPreviewUrl(
        this.dataset.shopUrl,
        location,
        window.Shopify?.theme,
      );
      const hint = this.querySelector("[data-native-secure-preview]");
      const link = this.querySelector("[data-native-secure-link]");
      if (host) host.hidden = localHttp || preview;
      if (hint) hint.hidden = !localHttp || preview;
      if (review) review.hidden = !preview;
      if (link) {
        link.hidden = !securePreview;
        if (securePreview) link.href = securePreview.href;
        else link.removeAttribute("href");
      }
      if (status) status.hidden = localHttp || preview;
      if (host && !localHttp && !preview) {
        const syncNativeForm = () => {
          const frame = host.querySelector("iframe");
          if (frame && frame !== this.ninjaFrame) {
            this.ninjaFrame = frame;
            this.prepareNinjaFrame(frame, signal, status);
          }
          if (this.accountEnabled) this.syncAccountPanel();
          else if (status) status.hidden = this.ninjaReady;
        };
        this.ninjaObserver = new MutationObserver(syncNativeForm);
        this.ninjaObserver.observe(host, { childList: true, subtree: true });
        this.ninjaStartTimeout = () => {
          clearTimeout(this.ninjaTimer);
          if (this.ninjaReady) return;
          this.ninjaTimer = setTimeout(() => {
            if (
              !this.isConnected ||
              signal.aborted ||
              !dialog.open ||
              this.ninjaReady
            )
              return;
            if (status) {
              status.hidden = false;
              status.textContent = this.dataset.ninjaUnavailable || "";
            }
          }, 12000);
        };
        syncNativeForm();
        if (dialog.open) this.ninjaStartTimeout();
      }
    }
    if (this.dataset.captureMode === "klaviyo") {
      const host = this.querySelector("[data-klaviyo-host]");
      const formId = this.dataset.klaviyoFormId || "";
      const status = this.querySelector("[data-klaviyo-status]");
      const review = this.querySelector("[data-klaviyo-review]");
      const preview = signupDemoAllowed();
      // Klaviyo's native SDK inherits HTTP locally; its API then redirects the
      // preflight to HTTPS, leaving Submit pending. Use Shopify's secure preview
      // rather than patching the SDK or intercepting contact/consent requests.
      const localHttp = local && location.protocol === "http:";
      const securePreview = secureSignupPreviewUrl(
        this.dataset.shopUrl,
        location,
        window.Shopify?.theme,
      );
      const secureHint = this.querySelector("[data-klaviyo-secure-preview]");
      const secureLink = this.querySelector("[data-klaviyo-secure-link]");
      if (secureHint) secureHint.hidden = preview || !localHttp;
      if (secureLink) {
        secureLink.hidden = !securePreview;
        if (securePreview) secureLink.href = securePreview.href;
        else secureLink.removeAttribute("href");
      }
      if (host) host.hidden = preview || localHttp;
      if (review) review.hidden = !preview;
      if (preview || localHttp) {
        if (status) status.hidden = true;
      } else if (host && /^[A-Za-z0-9_-]{3,32}$/.test(formId)) {
        const template = host.querySelector("template[data-klaviyo-template]");
        if (template) {
          host.append(template.content.cloneNode(true));
          template.remove();
        }
        const syncForm = () => {
          const ready = !!host.querySelector("form, input, iframe");
          if (ready) clearTimeout(this.klaviyoTimer);
          if (status) status.hidden = ready;
        };
        this.klaviyoObserver = new MutationObserver(syncForm);
        this.klaviyoObserver.observe(host, { childList: true, subtree: true });
        this.klaviyoTimer = setTimeout(() => {
          if (
            !this.isConnected ||
            signal.aborted ||
            host.querySelector("form, input, iframe")
          )
            return;
          if (status) {
            status.hidden = false;
            status.textContent = this.dataset.klaviyoUnavailable || "";
          }
        }, 12000);
        syncForm();
      } else {
        const status = this.querySelector("[data-klaviyo-status]");
        if (status) status.textContent = this.dataset.klaviyoUnavailable || "";
      }
    }
    this.addEventListener(
      "click",
      (event) => {
        if (
          event.target instanceof Element &&
          event.target.closest("[data-ninja-account-refresh]")
        )
          this.refreshAccount();
        if (
          event.target instanceof Element &&
          event.target.closest("[data-raptor-close]")
        )
          dialog.close();
        if (event.target === dialog) {
          const r = dialog.getBoundingClientRect();
          if (
            event.clientX < r.left ||
            event.clientX > r.right ||
            event.clientY < r.top ||
            event.clientY > r.bottom
          )
            dialog.close();
        }
        if (
          event.target instanceof Element &&
          event.target.closest('a[href^="#"]')
        ) {
          this.previousFocus = null;
          dialog.close();
        }
      },
      { signal },
    );
    dialog.addEventListener(
      "close",
      () => {
        clearTimeout(this.ninjaTimer);
        if (this.previousFocus?.isConnected)
          this.previousFocus.focus({ preventScroll: true });
      },
      { signal },
    );
    document.addEventListener(
      "click",
      (event) => {
        if (!(event.target instanceof Element)) return;
        const trigger = event.target.closest("[data-raptor-open-signup]");
        if (trigger) {
          event.preventDefault();
          this.open(trigger);
        }
      },
      { signal },
    );
    for (const trigger of document.querySelectorAll(
      "[data-raptor-open-signup]",
    ))
      trigger.hidden = false;
    // Auto-open waits for the merchant delay. Scrolling past 25% only opens it
    // after that delay, for visits where the timer was skipped (hidden tab,
    // focused form or another dialog).
    const delaySetting = Number.parseInt(this.dataset.openDelay ?? "", 10);
    const openDelay =
      (Number.isFinite(delaySetting) && delaySetting >= 0 ? delaySetting : 12) *
      1000;
    const openAfter = Date.now() + openDelay;
    this.timer = setTimeout(() => this.autoOpen(), openDelay);
    window.addEventListener(
      "scroll",
      () => {
        if (Date.now() < openAfter) return;
        const distance = document.documentElement.scrollHeight - innerHeight;
        if (distance > 0 && scrollY / distance >= 0.25) this.autoOpen();
      },
      { signal, passive: true },
    );
    if (window.Shopify?.designMode)
      this.closest(".shopify-section")?.addEventListener(
        "shopify:section:select",
        () => this.open(),
        { signal },
      );
    if (dialog.open && this.accountEnabled) this.refreshAccount();
  }

  syncAccountPanel() {
    if (!this.accountEnabled) return;
    const panel = this.querySelector("[data-ninja-account]");
    const total = this.querySelector("[data-ninja-account-total]");
    const status = this.querySelector("[data-ninja-account-status]");
    const refresh = this.querySelector("[data-ninja-account-refresh]");
    const host = this.querySelector("[data-ninja-host]");
    const confirmed = this.accountState === "confirmed";
    const missing = this.accountState === "missing";
    this.dataset.ninjaAccountState = this.accountState;
    this.dataset.ninjaRegistered = String(confirmed);
    if (host) host.hidden = !missing;
    if (panel) panel.hidden = missing;
    if (total) total.hidden = !confirmed;
    if (status)
      status.textContent =
        this.accountState === "loading"
          ? this.dataset.accountLoading || ""
          : this.accountState === "error"
            ? this.dataset.accountError || ""
            : "";
    if (refresh) {
      refresh.hidden = this.accountState !== "error";
      refresh.disabled = this.accountState === "loading";
    }
    const nativeStatus = this.querySelector("[data-ninja-status]");
    if (nativeStatus) nativeStatus.hidden = !missing || this.ninjaReady;
  }

  async refreshAccount() {
    if (!this.accountEnabled || this.accountRequest || !this.isConnected)
      return;
    const request = new AbortController();
    this.accountRequest = request;
    const customerId = this.dataset.customerId;
    this.accountState = "loading";
    this.syncAccountPanel();
    this.accountTimer = setTimeout(() => request.abort(), 12000);
    const current = () =>
      this.accountRequest === request &&
      this.isConnected &&
      customerId === this.dataset.customerId &&
      !request.signal.aborted;
    try {
      const response = await fetch("/apps/raptor-qa/account", {
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers: { Accept: "application/json" },
        signal: request.signal,
      });
      if (!response.ok) throw new Error("Account verification unavailable");
      const payload = await response.json();
      if (!current()) return;
      const outcome = ninjaAccountOutcome(
        payload,
        this.dataset.campaignId,
        customerId,
      );
      if (!outcome)
        throw new Error("Account identity or balance does not match");
      this.accountState =
        outcome === "missing" && this.nativeAccountRegistrationSeen
          ? "error"
          : outcome;
      if (outcome === "confirmed") {
        rememberRaptorEntry(this.dataset.campaignId);
        const count = this.querySelector("[data-ninja-account-count]");
        if (count)
          count.textContent = new Intl.NumberFormat(
            document.documentElement.lang || "en",
          ).format(payload.totalEntries);
      }
      this.syncAccountPanel();
      this.ninjaResize?.();
    } catch {
      if (
        this.accountRequest === request &&
        this.isConnected &&
        customerId === this.dataset.customerId
      ) {
        this.accountState = "error";
        this.syncAccountPanel();
      }
    } finally {
      if (this.accountRequest === request) {
        clearTimeout(this.accountTimer);
        this.accountRequest = null;
      }
    }
  }
  // The official embed writes an about:blank same-origin iframe. When it starts
  // inside a closed dialog its initial height can be 10px. Native registration
  // and awards stay vendor-owned; the visible accepted result unlocks optional SMS.
  prepareNinjaFrame(frame, signal, status) {
    if (typeof frame.addEventListener !== "function") return;
    this.ninjaFrameAbort?.abort();
    const frameAbort = new AbortController();
    this.ninjaFrameAbort = frameAbort;
    this.ninjaReady = false;
    if (!frame.getAttribute?.("title"))
      frame.setAttribute?.(
        "title",
        this.refs.dialog.getAttribute?.("aria-label") || "Giveaway signup",
      );
    const attach = () => {
      if (
        signal.aborted ||
        frameAbort.signal.aborted ||
        frame !== this.ninjaFrame
      )
        return;
      // A replacement or reloaded provider document must establish its own
      // participant before an existing native SMS form can be shown again.
      this.querySelector("raptor-sms-signup")?.clearParticipant?.();
      this.dataset.ninjaRegistered = "false";
      this.ninjaReady = false;
      if (status) {
        status.hidden = !this.refs.dialog.open;
        status.textContent = this.dataset.ninjaLoading || "";
      }
      if (this.refs.dialog.open) this.ninjaStartTimeout?.();
      this.ninjaResizeObserver?.disconnect();
      this.ninjaContentObserver?.disconnect();
      this.ninjaDocumentAbort?.abort();
      const documentAbort = new AbortController();
      this.ninjaDocumentAbort = documentAbort;
      this.ninjaResize = null;
      let doc;
      try {
        doc = frame.contentDocument;
      } catch {
        return;
      }
      if (!doc?.documentElement) return;
      const resize = () => {
        cancelAnimationFrame(this.ninjaResizeFrame);
        this.ninjaResizeFrame = requestAnimationFrame(() => {
          if (
            signal.aborted ||
            frameAbort.signal.aborted ||
            documentAbort.signal.aborted ||
            frame !== this.ninjaFrame ||
            !this.isConnected ||
            !this.refs.dialog.open
          )
            return;
          // documentElement includes the current iframe viewport and prevents
          // shrinking after the registration form changes to a shorter result.
          const height = Math.ceil(
            doc.body?.scrollHeight || doc.documentElement.scrollHeight,
          );
          if (height > 10 && frame.style.height !== `${height}px`)
            frame.style.height = `${height}px`;
          const container = frame.parentElement;
          if (
            height > 10 &&
            container?.id === "giveawayninja-iframe-container" &&
            container.style.height !== `${height}px`
          )
            container.style.height = `${height}px`;
          if (
            height > 10 &&
            doc.querySelector(
              'input:not([type="hidden"]), button, a[href], form',
            )
          ) {
            this.ninjaReady = true;
            clearTimeout(this.ninjaTimer);
            if (status) status.hidden = true;
          }
          const participant = nativeNinjaRegistration(doc);
          if (this.accountEnabled) {
            if (!participant) {
              const emailInput = doc.querySelector("#email-input");
              if (emailInput?.type === "email") {
                emailInput.value = this.dataset.customerEmail || "";
                emailInput.readOnly = true;
              }
            } else if (this.accountState === "missing") {
              // A cached native participant is never evidence of this Shopify
              // customer's balance. Only a matching user-submitted form refreshes it.
              if (
                participant.email?.toLowerCase() !==
                this.dataset.customerEmail?.toLowerCase()
              )
                this.accountState = "error";
              else if (!this.nativeAccountRegistrationSeen) {
                this.nativeAccountRegistrationSeen = true;
                this.refreshAccount();
              }
            }
            this.syncAccountPanel();
            return;
          }
          const registered = String(Boolean(participant));
          this.dataset.ninjaRegistered = registered;
          if (participant) rememberRaptorEntry(this.dataset.campaignId);
          const nativeDataset = doc.documentElement.dataset;
          if (nativeDataset && nativeDataset.raptorRegistered !== registered)
            nativeDataset.raptorRegistered = registered;
          if (participant && this.smsEnabled) {
            const sms = this.querySelector("raptor-sms-signup");
            if (typeof sms?.setParticipant === "function")
              sms.setParticipant(participant.email);
            else if (sms && !this.smsDefinitionPending) {
              this.smsDefinitionPending = true;
              customElements.whenDefined("raptor-sms-signup").then(() => {
                if (signal.aborted || !this.isConnected || !this.smsEnabled)
                  return;
                this.smsDefinitionPending = false;
                // A provider frame may have been replaced while the module
                // loaded. Re-read its current accepted state, never an old email.
                let currentParticipant;
                try {
                  currentParticipant = nativeNinjaRegistration(
                    this.ninjaFrame?.contentDocument,
                  );
                } catch {
                  return;
                }
                if (currentParticipant)
                  this.querySelector("raptor-sms-signup")?.setParticipant(
                    currentParticipant.email,
                  );
              });
            }
          } else if (this.ninjaReady)
            this.querySelector("raptor-sms-signup")?.clearParticipant?.();
        });
      };
      this.ninjaResize = resize;
      this.ninjaResizeObserver = new ResizeObserver(resize);
      this.ninjaResizeObserver.observe(doc.documentElement);
      this.ninjaContentObserver = new MutationObserver(resize);
      this.ninjaContentObserver.observe(doc.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
      frame.contentWindow?.addEventListener("resize", resize, {
        signal: documentAbort.signal,
      });
      doc.fonts?.ready.then(() => {
        if (
          !signal.aborted &&
          !frameAbort.signal.aborted &&
          !documentAbort.signal.aborted &&
          frame === this.ninjaFrame
        )
          resize();
      });
      resize();
    };
    frame.addEventListener("load", attach, { signal: frameAbort.signal });
    attach();
  }
  async autoOpen() {
    if (this.dataset.captureMode === "preview" && !signupDemoAllowed()) return;
    if (
      window.Shopify?.designMode ||
      RaptorSignupPopup.shown ||
      document.hidden ||
      this.autoOpenChecking ||
      this.autoOpenDeclined
    )
      return;
    try {
      if (sessionStorage.getItem("tn:raptor-signup-shown")) return;
    } catch {
      /* In-memory session guard remains available. */
    }
    if (
      document.querySelector("dialog[open]") ||
      document.activeElement?.closest("form, purchase-supply")
    )
      return;
    // Someone who already entered is not interrupted again. Clicking a
    // signup trigger still opens the popup and shows their entries.
    this.autoOpenChecking = true;
    let entered;
    try {
      entered = await this.alreadyEntered();
    } finally {
      this.autoOpenChecking = false;
    }
    if (entered) {
      this.autoOpenDeclined = true;
      return;
    }
    if (
      !this.isConnected ||
      RaptorSignupPopup.shown ||
      document.hidden ||
      document.querySelector("dialog[open]") ||
      document.activeElement?.closest("form, purchase-supply")
    )
      return;
    this.accountJustChecked = this.accountEnabled;
    this.open();
  }
  /**
   * Whether this visitor already entered the campaign, checked before an
   * automatic open. A signed-in customer's signed account response decides;
   * for guests, this browser's remembered entry or the native form's
   * signed-in panel decides. An unverifiable account counts as entered so
   * the popup never opens on an error state by itself.
   * @returns {Promise<boolean>}
   */
  async alreadyEntered() {
    const campaignId = this.dataset.campaignId;
    if (this.accountEnabled) {
      if (this.accountState !== "confirmed" && this.accountState !== "missing")
        await this.refreshAccount();
      return this.accountState !== "missing";
    }
    if (rememberedRaptorEntry(campaignId)) return true;
    let doc = null;
    try {
      doc =
        this.querySelector("[data-ninja-host] iframe")?.contentDocument ?? null;
    } catch {
      doc = null;
    }
    if (nativeNinjaEntered(doc)) {
      rememberRaptorEntry(campaignId);
      return true;
    }
    return false;
  }
  open(trigger = document.activeElement) {
    const dialog = this.refs.dialog;
    if (dialog.open) return;
    this.previousFocus = trigger instanceof HTMLElement ? trigger : null;
    RaptorSignupPopup.shown = true;
    try {
      sessionStorage.setItem("tn:raptor-signup-shown", "1");
    } catch {
      /* No persistent storage is required. */
    }
    clearTimeout(this.timer);
    dialog.showModal();
    // An automatic open has just verified the account; reuse that result.
    if (this.accountEnabled && !this.accountJustChecked) this.refreshAccount();
    this.accountJustChecked = false;
    this.ninjaStartTimeout?.();
    this.ninjaResize?.();
    this.dispatchEvent(
      new CustomEvent("raptor:signup-open", { bubbles: true }),
    );
  }
  disconnectedCallback() {
    this.accountRequest?.abort();
    this.accountRequest = null;
    clearTimeout(this.accountTimer);
    clearTimeout(this.timer);
    clearTimeout(this.klaviyoTimer);
    clearTimeout(this.ninjaTimer);
    if (this.refs.dialog?.open) this.refs.dialog.close();
    this.abort?.abort();
    this.klaviyoObserver?.disconnect();
    this.ninjaObserver?.disconnect();
    this.ninjaResizeObserver?.disconnect();
    this.ninjaContentObserver?.disconnect();
    this.ninjaFrameAbort?.abort();
    this.ninjaDocumentAbort?.abort();
    cancelAnimationFrame(this.ninjaResizeFrame);
    super.disconnectedCallback();
  }
}
if (!customElements.get("raptor-signup-popup"))
  customElements.define("raptor-signup-popup", RaptorSignupPopup);

// A single module listener serves current and section-rendered giveaway content.
// Native links remain the fallback; unrelated storefront links are untouched.
function raptorScrollTarget(url) {
  if (
    url.origin !== location.origin ||
    url.pathname !== location.pathname ||
    url.search !== location.search ||
    !url.hash
  )
    return null;
  try {
    const target = document.getElementById(
      decodeURIComponent(url.hash.slice(1)),
    );
    return target?.closest(".raptor-giveaway") ? target : null;
  } catch {
    return null;
  }
}
function scrollRaptorSection(target) {
  const dialog = document.querySelector("raptor-signup-popup dialog[open]");
  dialog?.close();
  const focusTarget = target.querySelector("h1, h2") || target;
  if (!focusTarget.hasAttribute("tabindex")) {
    focusTarget.setAttribute("tabindex", "-1");
    focusTarget.addEventListener(
      "blur",
      () => focusTarget.removeAttribute("tabindex"),
      { once: true },
    );
  }
  focusTarget.focus({ preventScroll: true });
  target.scrollIntoView({
    block: "start",
    behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "instant"
      : "smooth",
  });
  if (location.hash)
    history.replaceState(
      history.state,
      "",
      location.pathname + location.search,
    );
}
document.addEventListener("click", (event) => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    !(event.target instanceof Element)
  )
    return;
  const link = event.target.closest(".raptor-giveaway a[href]");
  if (
    !link ||
    link.hasAttribute("download") ||
    (link.target && link.target !== "_self") ||
    link.hasAttribute("data-raptor-open-signup")
  )
    return;
  const target = raptorScrollTarget(new URL(link.href, location.href));
  if (!target) return;
  event.preventDefault();
  scrollRaptorSection(target);
});
function restoreRaptorAnchor() {
  const target = raptorScrollTarget(new URL(location.href));
  if (target) scrollRaptorSection(target);
}
if (document.readyState === "complete") restoreRaptorAnchor();
else window.addEventListener("load", restoreRaptorAnchor, { once: true });
